import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Loader2, Lock, CheckCircle } from "lucide-react";
import { PasswordInput } from "@/shared/ui/password-input";
import { useToast } from "@/shared/ui/use-toast";
import { AuthLayout } from "./AuthLayout";
import { AuthCard, AuthCardHeader, AuthCardContent } from "./AuthCard";
import { LogoHeader, AuthHeader } from "./AuthHeader";
import { SuccessState } from "./SuccessState";
import { z } from "zod";
import { useResetPassword } from "../hooks/auth.hooks";

// Schema de validação
const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, { message: "A senha deve ter no mínimo 8 caracteres" })
    .regex(/[A-Z]/, { message: "A senha deve conter pelo menos uma letra maiúscula" })
    .regex(/[a-z]/, { message: "A senha deve conter pelo menos uma letra minúscula" })
    .regex(/[0-9]/, { message: "A senha deve conter pelo menos um número" })
    .regex(/[^A-Za-z0-9]/, { message: "A senha deve conter pelo menos um símbolo" }),
  passwordConfirmation: z.string()
}).refine((data) => data.password === data.passwordConfirmation, {
  message: "As senhas não coincidem",
  path: ["passwordConfirmation"],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPassword() {
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: "",
    passwordConfirmation: "",
  });
  const [errors, setErrors] = useState<{
    password?: string;
    passwordConfirmation?: string;
  }>({});

  const location = useLocation();
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  const accessToken = params.get("access_token");

  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    try {
      resetPasswordSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          const path = err.path[0] as string;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };
  const { toast } = useToast();
  const navigate = useNavigate();
  const { mutateAsync: resetPassword, isPending } = useResetPassword({
    onSuccess: () => {
      setIsSuccess(true);
      toast({
        title: "Senha redefinida!",
        description: "Sua senha foi redefinida com sucesso. Você já pode fazer login com a nova senha.",
        variant: "default",
      });

      setTimeout(() => {
        navigate("/auth");
      }, 3000);
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível redefinir sua senha. Verifique o token e tente novamente.",
        variant: "destructive",
      });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!accessToken) {
      toast({
        title: "Erro",
        description: "Token de redefinição inválido ou expirado, solicite um novo link de redefinição.",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    resetPassword({
      newPassword: formData.password,
      token: accessToken
    });
  };

  if (isSuccess) {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthCardHeader>
            <LogoHeader />
            <SuccessState
              title="Senha Redefinida!"
              description="Sua senha foi redefinida com sucesso. Você será redirecionado para o login em instantes."
              buttonText="Voltar para o login"
              redirectTo="/auth"
              icon={<CheckCircle className="h-8 w-8 text-green-500" />}
            />
          </AuthCardHeader>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthCardHeader>
          <LogoHeader />
          <AuthHeader
            title="Redefinir Senha"
            description="Digite sua nova senha abaixo."
          />
        </AuthCardHeader>
        <AuthCardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">
                Nova Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50 z-10" />
                <PasswordInput
                  id="password"
                  name="password"
                  placeholder="Digite sua nova senha"
                  className={`pl-10 bg-[#2A2A2A] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#D91657] focus-visible:ring-offset-0 ${errors.password ? 'border-red-500' : ''
                    }`}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation" className="text-white/80">
                Confirme a Nova Senha
              </Label>
              <div className="relative mt-4">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50 z-10" />
                <PasswordInput
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  placeholder="Confirme sua nova senha"
                  className={`pl-10 bg-[#2A2A2A] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#D91657] focus-visible:ring-offset-0 ${errors.passwordConfirmation ? 'border-red-500' : ''
                    }`}
                  value={formData.passwordConfirmation}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                {errors.passwordConfirmation && (
                  <p className="mt-1 text-sm text-red-500">{errors.passwordConfirmation}</p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#D91657] hover:bg-[#C1174F] text-white h-11 font-medium text-sm"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redefinindo...
                </>
              ) : (
                "Redefinir Senha"
              )}
            </Button>
          </form>
        </AuthCardContent>
      </AuthCard>
    </AuthLayout>
  );
}

export default ResetPassword;
